import { fetchMedia, FetchMediaError } from 'fetch-media';
import { QueryKey, useQuery, UseQueryOptions } from 'react-query';
import { ReadonlyDeep } from 'type-fest';
import { useLocale } from '../hooks/useLocale';
import { useToken } from '../hooks/useToken';
import { authorization } from '../utils/authorization';

export type ApiEventProgress = ReadonlyDeep<{
  event: {
    total_count: number;
    progress: number;
  };
}>;

export function useEventProgress(
  url: string | undefined | null,
  {
    enabled = true,
    onError,
    ...options
  }: UseQueryOptions<ApiEventProgress, FetchMediaError | Error> = {}
) {
  const { ref, token, logout } = useToken();
  const locale = useLocale();

  return useQuery<ApiEventProgress, FetchMediaError | Error>(
    ['tracks', locale, url, 'progress'] as QueryKey,
    async ({ signal }) => {
      if (!ref.current) {
        throw new Error('Expected logged in, actual: no token');
      }

      const response = await fetchMedia(url!, {
        headers: {
          accept: ['application/vnd.soundersmusic.event.v1.progress+json'].join(
            ', '
          ),
          acceptLanguage: locale,
          authorization: authorization(ref.current)!,
        },
        method: 'GET',
        disableFormData: true,
        disableFormUrlEncoded: true,
        signal,
      });

      if (response && typeof response === 'object') {
        if ('event' in response) {
          return response as ApiEventProgress;
        }

        throw new FetchMediaError(
          'Expected event progress in response, actual: ' +
            Object.keys(response).join(', '),
          { status: 500, statusText: '' }
        );
      }

      throw new FetchMediaError(
        'Expected event progress response, actual:' + typeof response,
        { status: 500, statusText: '' }
      );
    },
    {
      enabled: Boolean(url && token && enabled),

      onError(error) {
        if (error instanceof FetchMediaError) {
          if (error.response.status === 401) {
            logout();
          }
        }

        if (onError) {
          onError(error);
        }
      },

      ...options,
    }
  );
}
